import { useEffect, useState } from "react";
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  Database,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { fetchAutomationsHealth } from "../api";
import type { AutomationsHealthPayload } from "../types";

export function AutomationsHealthPanel() {
  const [data, setData] = useState<AutomationsHealthPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"tables" | "scheduled" | "gads">("tables");

  const loadHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAutomationsHealth();
      setData(res);
    } catch (err: any) {
      setError(err?.message || "Error al consultar estado de automatizaciones");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadHealth();
  }, []);

  return (
    <details className="panel disclosure-panel automations-health-panel" open>
      <summary className="automations-summary-header">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Clock size={16} aria-hidden="true" />
          <span>Salud de Automatizaciones & Scheduled Queries</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {data && (
            <span className={`health-pill ${data.overall_status === "VERDE" ? "is-good" : data.overall_status === "AMARILLO" ? "is-warning" : "is-danger"}`}>
              <span className="health-dot" />
              <span>{data.overall_status === "VERDE" ? "TODO AL DÍA" : data.overall_status === "AMARILLO" ? "RETRASO LEVE" : "ATENCIÓN"}</span>
            </span>
          )}
          <button
            type="button"
            className="button-ghost"
            style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem" }}
            onClick={(e) => {
              e.preventDefault();
              void loadHealth();
            }}
            disabled={loading}
            title="Refrescar estado de automatizaciones"
          >
            <RefreshCw size={12} className={loading ? "spin" : ""} />
            <span>Refrescar</span>
          </button>
        </div>
      </summary>

      <div style={{ padding: "1rem" }}>
        {error && (
          <div className="alert-card danger" style={{ marginBottom: "1rem" }}>
            <AlertCircle size={16} />
            <div>
              <strong>Error consultando automatizaciones:</strong> {error}
            </div>
          </div>
        )}

        {/* Pestañas de vista */}
        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", borderBottom: "1px solid var(--border, #333)", paddingBottom: "0.5rem" }}>
          <button
            type="button"
            className={`button ${activeTab === "tables" ? "button-primary" : "button-ghost"}`}
            style={{ fontSize: "0.8rem", padding: "0.3rem 0.8rem" }}
            onClick={() => setActiveTab("tables")}
          >
            <Database size={13} style={{ marginRight: "0.3rem" }} />
            Tablas del Lake ({data?.tables_freshness.length ?? 5})
          </button>
          <button
            type="button"
            className={`button ${activeTab === "scheduled" ? "button-primary" : "button-ghost"}`}
            style={{ fontSize: "0.8rem", padding: "0.3rem 0.8rem" }}
            onClick={() => setActiveTab("scheduled")}
          >
            <Calendar size={13} style={{ marginRight: "0.3rem" }} />
            Scheduled Queries ({data?.datalake_queries_count ?? 0})
            {data && data.datalake_queries_failed > 0 && (
              <span style={{ marginLeft: "0.4rem", color: "var(--danger, #f87171)" }}>({data.datalake_queries_failed} fallidas)</span>
            )}
          </button>
          <button
            type="button"
            className={`button ${activeTab === "gads" ? "button-primary" : "button-ghost"}`}
            style={{ fontSize: "0.8rem", padding: "0.3rem 0.8rem" }}
            onClick={() => setActiveTab("gads")}
          >
            Google Ads Transfers ({data?.gads_transfers_count ?? 0})
          </button>
        </div>

        {/* Tab 1: Tablas Centrales de Ingesta */}
        {activeTab === "tables" && (
          <div className="table-responsive">
            <table className="compact-table" style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", opacity: 0.7, borderBottom: "1px solid var(--border, #333)" }}>
                  <th style={{ padding: "0.5rem" }}>Tabla del Datalake</th>
                  <th style={{ padding: "0.5rem" }}>Dominio</th>
                  <th style={{ padding: "0.5rem" }}>Último Dato</th>
                  <th style={{ padding: "0.5rem" }}>Desfase</th>
                  <th style={{ padding: "0.5rem" }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr>
                    <td colSpan={5} style={{ padding: "24px 16px", textAlign: "center", color: "var(--muted, #8892b0)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                        <RefreshCw size={15} className="spin" />
                        <span>Consultando frescura de tablas en BigQuery...</span>
                      </span>
                    </td>
                  </tr>
                )}
                {data?.tables_freshness.map((t) => (
                  <tr key={t.table} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "0.5rem" }}>
                      <strong>{t.name}</strong>
                      <div style={{ fontSize: "0.75rem", opacity: 0.6 }}>{t.table}</div>
                    </td>
                    <td style={{ padding: "0.5rem" }}>
                      <span className="badge-chip">{t.domain}</span>
                    </td>
                    <td style={{ padding: "0.5rem", fontFamily: "var(--font-mono, monospace)" }}>
                      {t.max_date ?? "Sin datos"}
                    </td>
                    <td style={{ padding: "0.5rem" }}>
                      {t.days_behind != null ? (
                        <span>{t.days_behind <= 0 ? "Al día (0d)" : `${t.days_behind}d de retraso`}</span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={{ padding: "0.5rem" }}>
                      <span className={`health-pill ${t.status === "VERDE" ? "is-good" : t.status === "AMARILLO" ? "is-warning" : "is-danger"}`}>
                        <span className="health-dot" />
                        <span>{t.status}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 2: Scheduled Queries de BigQuery */}
        {activeTab === "scheduled" && (
          <div className="table-responsive" style={{ maxHeight: "360px", overflowY: "auto" }}>
            <table className="compact-table" style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", opacity: 0.7, borderBottom: "1px solid var(--border, #333)" }}>
                  <th style={{ padding: "0.5rem" }}>Consulta Programada</th>
                  <th style={{ padding: "0.5rem" }}>Frecuencia</th>
                  <th style={{ padding: "0.5rem" }}>Última Ejecución</th>
                  <th style={{ padding: "0.5rem" }}>Próxima Corrida</th>
                  <th style={{ padding: "0.5rem" }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr>
                    <td colSpan={5} style={{ padding: "24px 16px", textAlign: "center", color: "var(--muted, #8892b0)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                        <RefreshCw size={15} className="spin" />
                        <span>Consultando ejecuciones de Scheduled Queries...</span>
                      </span>
                    </td>
                  </tr>
                )}
                {data?.datalake_queries.map((q) => (
                  <tr key={q.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "0.5rem" }}>
                      <strong>{q.name}</strong>
                    </td>
                    <td style={{ padding: "0.5rem", fontSize: "0.75rem", opacity: 0.8 }}>
                      {q.schedule}
                    </td>
                    <td style={{ padding: "0.5rem", fontFamily: "var(--font-mono, monospace)", fontSize: "0.8rem" }}>
                      {q.last_run ?? "-"}
                    </td>
                    <td style={{ padding: "0.5rem", fontFamily: "var(--font-mono, monospace)", fontSize: "0.8rem", opacity: 0.7 }}>
                      {q.next_run ?? "-"}
                    </td>
                    <td style={{ padding: "0.5rem" }}>
                      {q.status === "EXITOSO" ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", color: "var(--good, #4ade80)" }}>
                          <CheckCircle2 size={14} />
                          <span>EXITOSO</span>
                        </span>
                      ) : q.status === "FALLIDO" ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", color: "var(--danger, #f87171)" }}>
                          <XCircle size={14} />
                          <span>FALLIDO</span>
                        </span>
                      ) : (
                        <span>{q.status}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 3: Google Ads Transfers */}
        {activeTab === "gads" && (
          <div className="table-responsive" style={{ maxHeight: "360px", overflowY: "auto" }}>
            <table className="compact-table" style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", opacity: 0.7, borderBottom: "1px solid var(--border, #333)" }}>
                  <th style={{ padding: "0.5rem" }}>Cuenta</th>
                  <th style={{ padding: "0.5rem" }}>Frecuencia</th>
                  <th style={{ padding: "0.5rem" }}>Última Ejecución</th>
                  <th style={{ padding: "0.5rem" }}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {loading && !data && (
                  <tr>
                    <td colSpan={4} style={{ padding: "24px 16px", textAlign: "center", color: "var(--muted, #8892b0)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                        <RefreshCw size={15} className="spin" />
                        <span>Consultando Google Ads Data Transfers...</span>
                      </span>
                    </td>
                  </tr>
                )}
                {data?.gads_transfers.map((q) => (
                  <tr key={q.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "0.5rem" }}>{q.name}</td>
                    <td style={{ padding: "0.5rem", fontSize: "0.75rem", opacity: 0.8 }}>{q.schedule}</td>
                    <td style={{ padding: "0.5rem", fontFamily: "var(--font-mono, monospace)", fontSize: "0.8rem" }}>{q.last_run ?? "-"}</td>
                    <td style={{ padding: "0.5rem" }}>
                      {q.status === "EXITOSO" ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", color: "var(--good, #4ade80)" }}>
                          <CheckCircle2 size={14} />
                          <span>EXITOSO</span>
                        </span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", color: "var(--danger, #f87171)" }}>
                          <XCircle size={14} />
                          <span>{q.status}</span>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </details>
  );
}
